<template>
	<div class="form-builder-renderer">
		<!-- Step 1 Fields -->
		<template v-if="step === 'step_1'">
			<template v-for="field in step1Fields" :key="field.id">
				<!-- Currency Selector -->
				<div v-if="field.type === 'currency_selector'" class="el-custom-select">
					<el-select 
						class="w-100" 
						v-model="store.currency" 
						@change="store.currency = $event"
					>
						<el-option 
							v-for="(curr, key) in currencies" 
							:value="key" 
							:key="key" 
							:label="curr"
						>
							{{ curr }}
						</el-option>
					</el-select>
				</div>

				<!-- Predefined Amounts -->
				<ul 
					v-if="field.type === 'predefined_amounts' && !store.recurring" 
					class="wpcm-pre-dfind-amt"
				>
					<li 
						v-for="(amount, index) in getAmounts(field)" 
						:key="index" 
						@click.prevent="store.amount = convert(parseFloat(amount))"
					>
						<a 
							href="#" 
							:class="['wpdonation-button', store.amount == convert(parseFloat(amount)) && 'active']"
						>
							{{ getSymbol() }}{{ convert(parseFloat(amount)) }}
						</a>
					</li>
				</ul>

				<!-- Custom Amount Input -->
				<div v-if="field.type === 'custom_amount'" class="wpcm-custm-amt-box">
					<label v-if="field.label" class="wpcm-field-label" style="display: block; margin-bottom: 8px; font-weight: 500;">{{ field.label }}</label>
					<div style="display: flex; align-items: center;">
						<span v-if="symbol" class="wpcm-symbl-prefix">{{ getSymbol() }}</span>
						<input 
							v-model.number="store.amount"
							@input="updateAmount($event)"
							@keypress="isNumber"
							:placeholder="field.placeholder || field.label || 'Enter amount'"
							style="flex: 1;"
						/>
					</div>
				</div>

				<!-- Recurring Toggle -->
				<div v-if="field.type === 'recurring_toggle'" class="wpcm-recurring-btns" style="margin: 15px 0;">
					<label v-if="field.label" class="wpcm-field-label" style="display: block; margin-bottom: 8px; font-weight: 500;">{{ field.label }}</label>
					<ul class="d-inline-flex" style="list-style: none; padding: 0; margin: 0; gap: 10px;">
						<li class="m-0" @click.prevent="setRecurring(false)" style="margin: 0;">
							<a href="#" :class="['wpdonation-button', !store.recurring && 'active']" style="text-decoration: none; padding: 10px 20px; display: inline-block; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;">
								{{ field.one_time_label || strings?.one_time || 'One Time' }}
							</a>
						</li>
						<li class="m-0" @click.prevent="setRecurring(true)" style="margin: 0;">
							<a href="#" :class="['wpdonation-button', store.recurring && 'active']" style="text-decoration: none; padding: 10px 20px; display: inline-block; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;">
								{{ field.recurring_label || strings?.recurring || 'Recurring' }}
							</a>
						</li>
					</ul>
				</div>

				<!-- Causes Dropdown -->
				<div v-if="field.type === 'causes_dropdown'" class="el-custom-select">
					<el-select 
						class="w-100" 
						v-model="store.selectedCause" 
						@change="setValue('selectedCause', $event)" 
						:placeholder="field.required ? 'Select Any Cause *' : 'Select Any Cause'"
						:required="field.required"
					>
						<el-option 
							v-for="(cause, key) in causes_list" 
							:key="key" 
							:value="key" 
							:label="cause"
						>
							{{ cause }}
						</el-option>
					</el-select>
				</div>

				<!-- Honoree Dropdown -->
				<div v-if="field.type === 'honoree_dropdown'" class="el-custom-select">
					<el-select 
						class="w-100" 
						v-model="store.extras.donation_custom_dropdown"
						@change="setValue('donation_custom_dropdown', $event)" 
						:placeholder="field.placeholder || 'Select Any Honoree'"
						:required="field.required"
					>
						<el-option 
							v-for="(honoree, key) in donation_custom_dropdown" 
							:value="key" 
							:key="key" 
							:label="honoree"
						>
							{{ honoree }}
						</el-option>
					</el-select>
				</div>
			</template>
		</template>

		<!-- Step 2 Fields (Billing) -->
		<template v-if="step === 'step_2'">
			<FormBuilderFields 
				v-if="step2Fields.length > 0"
				:billing-form-data="billingFormData"
				:form-fields="step2Fields"
			/>
		</template>

		<!-- Step 3 Fields (Payment) -->
		<template v-if="step === 'step_3'">
			<template v-for="field in step3Fields" :key="field.id">
				<!-- Payment Gateways -->
				<div v-if="field.type === 'payment_gateways'" class="wpcm-payment-gateways">
					<div class="mb-3">
						<ul class="wpcm-gateway-list" v-if="gateways">
							<li v-for="(gateway, gateway_id) in gateways" :key="gateway_id">
								<a
									@click.prevent="store.payment_method = gateway.id"
									:class="[(gateway.id === store.payment_method) && 'active']"
									title=""
									href="#"
								>
									<span v-if="gateway.icon"><img :src="gateway.icon" alt=""></span>
									{{ gateway.title ? gateway.title : gateway.name }}
								</a>
							</li>
						</ul>
					</div>
					<div
						class="wpcm-gateway-testing-info mb-3"
						v-if="selectedTestingInfo"
					>
						<div
							class="wpcm-gateway-testing-info__text"
							v-html="selectedTestingInfo"
						></div>
					</div>
				</div>
			</template>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useDonationFormsStore } from '../store';
import FormBuilderFields from './FormBuilderFields.vue';

const props = defineProps({
	formBuilderConfig: {
		type: Object,
		required: true
	},
	step: {
		type: String,
		required: true
	},
	currencies: Object,
	symbols: Object,
	symbol: String,
	strings: Object,
	show_recurring: Boolean,
	causes_list: Object,
	donation_custom_dropdown: Object,
	gateways: Object,
	billingFormData: Object
});

const store = useDonationFormsStore();
const lifeline_donation = globalThis.lifeline_donation;
const _ = globalThis._;

const step1Fields = computed(() => {
	if (!props.formBuilderConfig?.fields) return [];
	return props.formBuilderConfig.fields.filter((f: any) => f.step === 'step_1');
});

const step2Fields = computed(() => {
	if (!props.formBuilderConfig?.fields) return [];
	return props.formBuilderConfig.fields.filter((f: any) => f.step === 'step_2');
});

const step3Fields = computed(() => {
	if (!props.formBuilderConfig?.fields) return [];
	return props.formBuilderConfig.fields.filter((f: any) => f.step === 'step_3');
});

const selectedTestingInfo = computed(() => {
	const selected = store.payment_method;
	if (!selected || !props.gateways) return '';
	const list: any = props.gateways;
	const match: any = Array.isArray(list)
		? list.find((g: any) => g && g.id === selected)
		: Object.values(list).find((g: any) => g && g.id === selected);
	return (match && match.testing_info) ? match.testing_info : '';
});

const getSymbol = () => {
	return props.symbols?.[store.currency] ?? props.symbol;
};

const convert = (amt: number) => {
	const { exchange_rates } = lifeline_donation;
	const syb = store.currency;
	if (_.get(exchange_rates, syb)) {
		return (_.get(exchange_rates, syb) * amt).toFixed(2);
	}
	return amt;
};

const getAmounts = (field: any) => {
	return field.amounts || [];
};

const setValue = (key: string, val: string | number) => {
	store[key] = val;
};

const setRecurring = (value: boolean) => {
	store.recurring = value;
};

const isNumber = (evt: KeyboardEvent) => {
	const charCode = evt.which || evt.keyCode;
	if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
		evt.preventDefault();
	}
};

const updateAmount = (event: Event) => {
	const target = event.target as HTMLInputElement;
	if (target.value) {
		store.amount = parseFloat(target.value);
	} else {
		store.amount = null;
	}
};
</script>

<style scoped>
.form-builder-renderer {
	width: 100%;
}
</style>